/**
 * Barcode Generator Control
 * Generates scannable barcodes using jsbarcode library
 */

import React, { memo, useRef, useEffect } from 'react';
import JsBarcode from 'jsbarcode';
import type { ControlRendererProps } from '../types';

interface BarcodeControlConfig {
  value?: string;
  format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'ITF';
  width?: number;
  height?: number;
  displayValue?: boolean;
  fontSize?: number;
  margin?: number;
  lineColor?: string;
  backgroundColor?: string;
  enableDownload?: boolean;
  downloadFormat?: 'png' | 'jpeg' | 'svg';
}

const BarcodeControl = memo(({ control, value, onChange, mode }: ControlRendererProps) => {
  const config = (control.config as BarcodeControlConfig) || {};
  const format = config.format ?? 'CODE128';
  const width = config.width ?? 2;
  const height = config.height ?? 100;
  const displayValue = config.displayValue ?? true;
  const fontSize = config.fontSize ?? 14;
  const margin = config.margin ?? 10;
  const lineColor = config.lineColor ?? '#000000';
  const backgroundColor = config.backgroundColor ?? '#FFFFFF';
  const enableDownload = config.enableDownload ?? true;
  const barcodeValue = value || config.value || '';

  // Use callback ref to ensure barcode generation fires when SVG is attached
  // This fixes ref-timing issues when switching between modes
  const svgCallbackRef = React.useCallback((node: SVGSVGElement | null) => {
    if (node && barcodeValue) {
      try {
        JsBarcode(node, barcodeValue, {
          format,
          width,
          height,
          displayValue,
          fontSize,
          margin,
          lineColor,
          background: backgroundColor,
        });
      } catch (err) {
        console.error('[BarcodeControl] Failed to generate barcode:', err);
      }
    }
  }, [barcodeValue, format, width, height, displayValue, fontSize, margin, lineColor, backgroundColor]);

  // In view mode, only show the barcode
  if (mode === 'view' || mode === 'preview') {
    if (!barcodeValue) {
      return <div style={{ color: '#999', fontSize: '14px' }}>No barcode data</div>;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', overflow: 'auto' }}>
        <div style={{ padding: margin, backgroundColor, borderRadius: '8px' }}>
          <svg ref={svgCallbackRef} />
        </div>
      </div>
    );
  }

  // In edit/design mode, show input + preview
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
          {control.label || 'Barcode'}
        </label>
        <textarea
          value={barcodeValue}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Enter text to encode as barcode"
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'monospace',
            minHeight: '60px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {barcodeValue && (
        <div style={{
          border: '1px solid #eee',
          padding: '16px',
          borderRadius: '4px',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          overflow: 'auto',
        }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>Barcode Preview</h4>
          <div style={{ padding: margin, backgroundColor, borderRadius: '8px' }}>
            <svg ref={svgCallbackRef} />
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
            Format: {format} | ✅ Scannable
          </div>
        </div>
      )}

      {!barcodeValue && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f9f9f9',
          border: '2px dashed #ddd',
          borderRadius: '4px',
          textAlign: 'center',
          color: '#999',
          fontSize: '13px',
        }}>
          Enter text above to generate barcode
        </div>
      )}
    </div>
  );
});

BarcodeControl.displayName = 'BarcodeControl';

export { BarcodeControl };
export type { BarcodeControlConfig };
